import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Smartphone, Check, Apple, Monitor, X, Sparkles, Zap } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import { trackDownload } from '../utils/analytics';

// Hook to manage PWA installation
export const useInstallApp = () => {
  // Initialize with computed values for SSR safety
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  });
  const [isIOS, setIsIOS] = useState(() => {
    if (typeof window === 'undefined') return false;
    const ua = navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(ua) && !window.MSStream;
  });
  const [isAndroid, setIsAndroid] = useState(() => {
    if (typeof window === 'undefined') return false;
    return /android/.test(navigator.userAgent.toLowerCase());
  });
  const [canInstall, setCanInstall] = useState(() => {
    if (typeof window === 'undefined') return false;
    const ua = navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod|android/.test(ua);
  });

  useEffect(() => {
    // Skip if already installed
    if (isInstalled) return;

    // Listen for beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setCanInstall(true);
      console.log('PWA: Install prompt captured');
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
      setCanInstall(false);
      toast.success("Skybot installed successfully! Find it on your home screen.");
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled]);

  const triggerInstall = async () => {
    if (installPrompt) {
      try {
        installPrompt.prompt();
        const { outcome } = await installPrompt.userChoice;
        
        if (outcome === 'accepted') {
          setIsInstalled(true);
          setInstallPrompt(null);
          // Track the download
          trackDownload({ method: 'pwa_prompt', platform: isAndroid ? 'android' : isIOS ? 'ios' : 'desktop' });
          return { success: true };
        }
        return { success: false, dismissed: true };
      } catch (error) {
        console.error('Install error:', error);
        return { success: false, error };
      }
    }
    
    return { success: false, noPrompt: true, isIOS, isAndroid };
  };

  return { installPrompt, isInstalled, isIOS, isAndroid, canInstall, triggerInstall };
};

// Installation Step Component
const InstallStep = ({ number, text }) => (
  <div 
    className="flex items-center gap-4 p-3 rounded-xl"
    style={{
      background: 'linear-gradient(145deg, rgba(40,45,60,0.5) 0%, rgba(30,35,50,0.5) 100%)',
      border: '1px solid rgba(255,255,255,0.05)'
    }}
  >
    <div 
      className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm"
      style={{
        background: 'linear-gradient(145deg, #22c55e 0%, #16a34a 100%)',
        color: '#000',
        boxShadow: '0 4px 10px rgba(34,197,94,0.3)'
      }}
    >
      {number}
    </div>
    <p className="text-sm text-white flex-1">{text}</p>
  </div>
);

// Benefit Item for Full Screen Prompt
const BenefitItem = ({ icon: Icon, text, color }) => (
  <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
    <div 
      className="w-10 h-10 rounded-lg flex items-center justify-center"
      style={{ background: `${color}20` }}
    >
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <span className="font-exo2 text-white text-sm">{text}</span>
  </div>
);

// Full Screen Install Prompt Component (moved outside)
const FullScreenInstallPrompt = ({ onInstall, onDismiss }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[200] flex items-center justify-center p-4"
    style={{
      background: 'linear-gradient(180deg, rgba(2,4,10,0.98) 0%, rgba(10,15,30,0.99) 100%)'
    }}
  >
    <motion.div
      initial={{ scale: 0.8, y: 50 }}
      animate={{ scale: 1, y: 0 }}
      transition={{ type: 'spring', damping: 20 }}
      className="max-w-sm w-full text-center"
    >
      {/* Animated App Icon */}
      <motion.div
        className="w-28 h-28 mx-auto mb-6 rounded-3xl flex items-center justify-center relative"
        style={{
          background: 'linear-gradient(145deg, rgba(0,242,234,0.2) 0%, rgba(0,150,136,0.1) 100%)',
          boxShadow: '0 20px 60px rgba(0,242,234,0.3), inset 0 2px 4px rgba(255,255,255,0.1)'
        }}
        animate={{ 
          y: [0, -10, 0],
          boxShadow: [
            '0 20px 60px rgba(0,242,234,0.3)',
            '0 30px 80px rgba(0,242,234,0.5)',
            '0 20px 60px rgba(0,242,234,0.3)'
          ]
        }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <Smartphone className="w-14 h-14 text-cyan-400" />
        <motion.div
          className="absolute -top-2 -right-2 w-10 h-10 rounded-xl flex items-center justify-center"
          style={{
            background: 'linear-gradient(145deg, #22c55e 0%, #16a34a 100%)',
            boxShadow: '0 4px 15px rgba(34,197,94,0.5)'
          }}
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
        >
          <Download className="w-5 h-5 text-white" />
        </motion.div>
      </motion.div>

      <h2 className="font-unbounded font-bold text-3xl text-white mb-3">
        Install Skybot
      </h2>
      <p className="font-exo2 text-slate-400 mb-8 text-lg">
        Get the full app experience with one tap!
      </p>

      {/* Benefits */}
      <div className="space-y-3 mb-8 text-left">
        <BenefitItem icon={Zap} text="Lightning fast launch from home screen" color="#fbbf24" />
        <BenefitItem icon={Sparkles} text="Works offline - scan anywhere" color="#00f2ea" />
        <BenefitItem icon={Smartphone} text="Full screen app mode - no browser UI" color="#22c55e" />
      </div>

      {/* Install Button */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onInstall}
        className="w-full py-5 rounded-2xl font-unbounded font-bold text-xl text-black mb-4"
        style={{
          background: 'linear-gradient(145deg, #22c55e 0%, #16a34a 100%)',
          boxShadow: '0 10px 40px rgba(34,197,94,0.5), inset 0 2px 4px rgba(255,255,255,0.3)'
        }}
      >
        <span className="flex items-center justify-center gap-3">
          <Download className="w-6 h-6" />
          Install Now - It&apos;s Free!
        </span>
      </motion.button>

      {/* Skip Button */}
      <button
        onClick={onDismiss}
        className="text-slate-500 hover:text-slate-300 transition-colors font-exo2"
      >
        Maybe Later
      </button>
    </motion.div>
  </motion.div>
);

// Prominent Download App Button with 3D Effect
const DownloadAppButton = ({ 
  variant = 'default', // 'default', 'sidebar', 'hero', 'floating'
  className = '',
  showWhenInstalled = false 
}) => {
  const { theme } = useTheme();
  const { isInstalled, isIOS, isAndroid, canInstall, triggerInstall } = useInstallApp();
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  const handleDownload = async () => {
    if (isInstalled) {
      toast.info("Skybot is already installed on your device!");
      return;
    }

    setIsInstalling(true);
    const result = await triggerInstall();
    setIsInstalling(false);

    if (result.success) {
      toast.success("Skybot installed! Check your home screen.");
    } else if (result.dismissed) {
      toast.info("Installation cancelled. You can install anytime!");
    } else if (result.isIOS || result.isAndroid || result.noPrompt) {
      // Show installation instructions modal
      setShowInstallModal(true);
    }
  };

  // Don't show if installed
  if (isInstalled && !showWhenInstalled) {
    return (
      <div 
        className="flex items-center gap-2 px-4 py-2 rounded-xl"
        style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)' }}
      >
        <Check className="w-4 h-4 text-green-400" />
        <span className="font-exo2 text-sm text-green-400">Installed</span>
      </div>
    );
  }

  // Variant styles
  const getVariantStyles = () => {
    switch (variant) {
      case 'hero':
        return {
          className: `flex items-center gap-3 px-8 py-4 rounded-2xl font-unbounded font-bold text-lg transition-all ${className}`,
          style: {
            background: 'linear-gradient(145deg, #22c55e 0%, #16a34a 100%)',
            color: 'white',
            boxShadow: '0 10px 40px rgba(34,197,94,0.5), inset 0 1px 0 rgba(255,255,255,0.3)',
            border: '2px solid rgba(255,255,255,0.1)'
          }
        };
      case 'sidebar':
        return {
          className: `w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all ${className}`,
          style: {
            background: 'linear-gradient(145deg, rgba(34,197,94,0.15) 0%, rgba(22,163,74,0.1) 100%)',
            color: '#22c55e',
            boxShadow: '0 4px 15px rgba(34,197,94,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
            border: '1px solid rgba(34,197,94,0.3)'
          }
        };
      case 'floating':
        return {
          className: `flex items-center gap-3 px-6 py-4 rounded-2xl font-unbounded font-bold transition-all ${className}`,
          style: {
            background: 'linear-gradient(145deg, #22c55e 0%, #16a34a 100%)',
            color: 'white',
            boxShadow: '0 15px 50px rgba(34,197,94,0.6), inset 0 1px 0 rgba(255,255,255,0.3)',
            border: '2px solid rgba(255,255,255,0.2)'
          }
        };
      default:
        return {
          className: `flex items-center gap-3 px-6 py-3 rounded-2xl font-unbounded font-bold transition-all ${className}`,
          style: {
            background: 'linear-gradient(145deg, #22c55e 0%, #16a34a 100%)',
            color: 'white',
            boxShadow: '0 8px 30px rgba(34,197,94,0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        };
    }
  };

  const variantStyles = getVariantStyles();

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.03, y: -2 }}
        whileTap={{ scale: 0.97 }}
        onClick={handleDownload}
        disabled={isInstalling}
        className={variantStyles.className}
        style={variantStyles.style}
        data-testid="download-app-btn"
      >
        {/* 3D Highlight */}
        <div 
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, transparent 50%)' }}
        />
        
        {isInstalling ? (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            >
              <Download className="w-5 h-5" />
            </motion.div>
            <span>Installing...</span>
          </>
        ) : (
          <>
            <Download className="w-5 h-5" />
            <span>{variant === 'sidebar' ? <span className="hidden lg:inline">Install App</span> : 'Install App'}</span>
            {(isIOS || isAndroid) && (
              <span className="text-xs opacity-70">
                {isIOS ? '(iOS)' : '(Android)'}
              </span>
            )}
          </>
        )}
      </motion.button>

      {/* Installation Instructions Modal */}
      <AnimatePresence>
        {showInstallModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
            onClick={() => setShowInstallModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="max-w-md w-full p-6 rounded-3xl relative overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, rgba(20,25,40,0.98) 0%, rgba(10,15,30,0.99) 100%)',
                border: '1px solid rgba(34,197,94,0.3)',
                boxShadow: '0 25px 80px rgba(0,0,0,0.8), 0 0 40px rgba(34,197,94,0.2)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Background Glow */}
              <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'radial-gradient(circle at 50% 0%, rgba(34,197,94,0.15) 0%, transparent 50%)'
                }}
              />

              {/* Close Button */}
              <button
                onClick={() => setShowInstallModal(false)}
                className="absolute top-4 right-4 p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>

              {/* Content */}
              <div className="relative text-center">
                {/* Icon */}
                <motion.div 
                  className="w-20 h-20 mx-auto mb-5 rounded-2xl flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(145deg, rgba(34,197,94,0.2) 0%, rgba(34,197,94,0.05) 100%)',
                    boxShadow: '0 10px 30px rgba(34,197,94,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
                  }}
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {isIOS ? (
                    <Apple className="w-10 h-10 text-green-400" />
                  ) : isAndroid ? (
                    <Smartphone className="w-10 h-10 text-green-400" />
                  ) : (
                    <Monitor className="w-10 h-10 text-green-400" />
                  )}
                </motion.div>

                <h3 className="font-unbounded font-bold text-2xl text-white mb-2">
                  Install Skybot
                </h3>
                <p className="text-slate-400 text-sm mb-6">
                  Add Skybot to your {isIOS ? 'iPhone/iPad' : isAndroid ? 'Android device' : 'desktop'} for quick access
                </p>

                {/* Installation Steps */}
                <div className="space-y-3 text-left mb-6">
                  {isIOS ? (
                    <>
                      <InstallStep number={1} text={<>Tap the <strong>Share</strong> button at the bottom of Safari</>} />
                      <InstallStep number={2} text={<>Scroll down and tap <strong>&ldquo;Add to Home Screen&rdquo;</strong></>} />
                      <InstallStep number={3} text={<>Tap <strong>&ldquo;Add&rdquo;</strong> in the top right corner</>} />
                    </>
                  ) : isAndroid ? (
                    <>
                      <InstallStep number={1} text={<>Tap the <strong>menu icon</strong> (⋮) in Chrome</>} />
                      <InstallStep number={2} text={<>Tap <strong>&ldquo;Add to Home screen&rdquo;</strong> or <strong>&ldquo;Install app&rdquo;</strong></>} />
                      <InstallStep number={3} text={<>Tap <strong>&ldquo;Install&rdquo;</strong> to confirm</>} />
                    </>
                  ) : (
                    <>
                      <InstallStep number={1} text={<>Click the <strong>install icon</strong> in the address bar</>} />
                      <InstallStep number={2} text={<>Or open browser menu → <strong>&ldquo;Install Skybot&rdquo;</strong></>} />
                      <InstallStep number={3} text={<>Click <strong>&ldquo;Install&rdquo;</strong> to add to your desktop</>} />
                    </>
                  )}
                </div>

                {/* Features Preview */}
                <div className="flex justify-center gap-4 mb-6 text-xs text-slate-500">
                  <div className="flex items-center gap-1">
                    <Zap className="w-3 h-3 text-yellow-400" />
                    <span>Fast Launch</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-cyan-400" />
                    <span>Offline Mode</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Smartphone className="w-3 h-3 text-green-400" />
                    <span>Native Feel</span>
                  </div>
                </div>

                {/* Got It Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowInstallModal(false)}
                  className="w-full py-4 rounded-2xl font-unbounded font-bold text-black transition-all"
                  style={{
                    background: 'linear-gradient(145deg, #22c55e 0%, #16a34a 100%)',
                    boxShadow: '0 8px 25px rgba(34,197,94,0.4), inset 0 1px 0 rgba(255,255,255,0.3)'
                  }}
                >
                  Got It!
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Floating Install Banner (shows at bottom of screen)
export const FloatingInstallBanner = ({ forceShow = false }) => {
  const { isInstalled, canInstall, triggerInstall, isIOS, isAndroid, installPrompt } = useInstallApp();
  const [showModal, setShowModal] = useState(false);
  const [showFullScreenPrompt, setShowFullScreenPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !!localStorage.getItem('skybot-install-dismissed');
  });

  // Check if dismissal has expired (after 24 hours)
  useEffect(() => {
    const checkDismissExpiry = () => {
      const dismissedFlag = localStorage.getItem('skybot-install-dismissed');
      const lastDismissed = localStorage.getItem('skybot-install-dismissed-time');
      
      if (dismissedFlag && lastDismissed) {
        const hoursSinceDismiss = (Date.now() - parseInt(lastDismissed)) / (1000 * 60 * 60);
        if (hoursSinceDismiss > 24) {
          localStorage.removeItem('skybot-install-dismissed');
          localStorage.removeItem('skybot-install-dismissed-time');
          setDismissed(false);
        }
      }
    };
    checkDismissExpiry();
  }, []);

  // Auto-show full screen prompt on first visit for mobile
  useEffect(() => {
    if (!isInstalled && (isIOS || isAndroid) && canInstall) {
      const hasSeenPrompt = localStorage.getItem('skybot-seen-install-prompt');
      if (!hasSeenPrompt) {
        const timer = setTimeout(() => {
          setShowFullScreenPrompt(true);
          localStorage.setItem('skybot-seen-install-prompt', 'true');
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [isInstalled, isIOS, isAndroid, canInstall]);

  // Auto-trigger native prompt if available
  useEffect(() => {
    if (installPrompt && !isInstalled) {
      const hasAutoTriggered = sessionStorage.getItem('skybot-auto-install-triggered');
      if (!hasAutoTriggered) {
        const timer = setTimeout(async () => {
          sessionStorage.setItem('skybot-auto-install-triggered', 'true');
          try {
            installPrompt.prompt();
            const { outcome } = await installPrompt.userChoice;
            if (outcome === 'accepted') {
              toast.success("Skybot installed! Find it on your home screen.");
            }
          } catch (e) {
            console.log('Auto-install prompt error:', e);
          }
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [installPrompt, isInstalled]);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('skybot-install-dismissed', 'true');
    localStorage.setItem('skybot-install-dismissed-time', Date.now().toString());
  };

  const handleInstall = async () => {
    const result = await triggerInstall();
    if (result.success) {
      setDismissed(true);
      setShowFullScreenPrompt(false);
    } else if (result.isIOS || result.isAndroid || result.noPrompt) {
      setShowModal(true);
    }
  };

  // Show full screen prompt for first-time mobile visitors
  if (showFullScreenPrompt && !isInstalled) {
    return (
      <FullScreenInstallPrompt 
        onInstall={handleInstall} 
        onDismiss={() => {
          setShowFullScreenPrompt(false);
          handleDismiss();
        }} 
      />
    );
  }

  if (isInstalled || dismissed || (!canInstall && !forceShow)) return null;

  return (
    <>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96"
      >
        <div 
          className="p-4 rounded-2xl flex items-center gap-4"
          style={{
            background: 'linear-gradient(145deg, rgba(20,25,40,0.98) 0%, rgba(10,15,30,0.99) 100%)',
            border: '1px solid rgba(34,197,94,0.3)',
            boxShadow: '0 15px 50px rgba(0,0,0,0.6), 0 0 30px rgba(34,197,94,0.15)'
          }}
        >
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: 'linear-gradient(145deg, rgba(34,197,94,0.2) 0%, rgba(34,197,94,0.05) 100%)',
              boxShadow: '0 4px 15px rgba(34,197,94,0.2)'
            }}
          >
            <Download className="w-6 h-6 text-green-400" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="font-unbounded font-bold text-white text-sm">Install Skybot</h4>
            <p className="font-exo2 text-xs text-slate-400 truncate">Add to home screen for quick access</p>
          </div>
          
          <div className="flex gap-2 flex-shrink-0">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleInstall}
              className="px-4 py-2 rounded-xl font-bold text-sm"
              style={{
                background: 'linear-gradient(145deg, #22c55e 0%, #16a34a 100%)',
                color: '#000',
                boxShadow: '0 4px 15px rgba(34,197,94,0.4)'
              }}
            >
              Install
            </motion.button>
            <button
              onClick={handleDismiss}
              className="p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Show install modal when needed */}
      <AnimatePresence>
        {showModal && (
          <DownloadAppButton variant="floating" showWhenInstalled />
        )}
      </AnimatePresence>
    </>
  );
};

export default DownloadAppButton;
